import { useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/apiClient';
import { formaterDate } from '../lib/format';
import { Badge, EnTetePage } from '../components/Elements';
import { NOUVEAUTES, VERSION_COURANTE, type Changement } from '../nouveautes';

/**
 * NOUVEAUTES (2026-10-02) : ce qui a changé pour les utilisateurs, version
 * par version, la plus récente en haut. Le contenu vit dans
 * `nouveautes.ts` ; l'écran ne fait que le filtrer et le mettre en page.
 *
 * Chaque changement n'est montré qu'à qui détient la permission qu'il
 * porte : une secrétaire ne lit pas les nouveautés de la paie. Une
 * version dont rien ne reste après ce filtre disparaît tout entière.
 *
 * Ouvrir la page marque la version de tête comme lue — c'est ce qui
 * éteint le point sur l'avatar, pour cet utilisateur, sur tous ses postes.
 */
export function NouveautesPage() {
  const { session, peut, rafraichir } = useAuth();

  useEffect(() => {
    if (!session || session.utilisateur.nouveautesLues === VERSION_COURANTE) return;
    void api.patch('/api/auth/profil', { nouveautesLues: VERSION_COURANTE }).then(() => rafraichir());
  }, [session, rafraichir]);

  const visible = (c: Changement) =>
    c.permission === undefined ||
    (Array.isArray(c.permission) ? peut(...c.permission) : peut(c.permission));

  const versions = NOUVEAUTES.map((v) => ({ ...v, changements: v.changements.filter(visible) })).filter(
    (v) => v.changements.length > 0,
  );

  return (
    <>
      <EnTetePage
        titre="Nouveautés"
        description="Ce qui a changé dans l’application, version par version, de la plus récente à la plus ancienne."
      />

      <div className="max-w-3xl space-y-4">
        {versions.map((v, index) => {
          // Les rubriques dans l'ordre de leur premiere apparition.
          const rubriques = [...new Set(v.changements.map((c) => c.ecran))];
          return (
            <section key={v.version} className={`carte px-5 py-4 ${index > 0 ? 'text-encre-600' : ''}`}>
              <h2 className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-base font-semibold text-encre-900">Version {v.version}</span>
                <span className="text-sm text-encre-500">
                  {v.date ? `déployée le ${formaterDate(v.date)}` : 'pas encore déployée'}
                </span>
                {index === 0 && v.date && <Badge ton="succes">version actuelle</Badge>}
              </h2>
              {rubriques.map((rubrique) => (
                <div key={rubrique} className="mt-3">
                  <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-encre-500">{rubrique}</h3>
                  <ul className="divide-y divide-encre-100">
                    {v.changements
                      .filter((c) => c.ecran === rubrique)
                      .map((c) => (
                        <li key={c.titre} className="py-1.5 text-sm leading-relaxed">
                          <span className="font-semibold text-encre-900">{c.titre}.</span> {c.texte}
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </section>
          );
        })}
        {versions.length === 0 && (
          <p className="rounded-lg border border-dashed border-encre-200 px-4 py-3 text-sm text-encre-500">
            Rien de nouveau pour vos écrans.
          </p>
        )}
      </div>
    </>
  );
}
